import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Camera, Paperclip, FileText, X, Loader2, AlertTriangle } from 'lucide-react';
import { portalService } from '../../services/portal';
import CampoCidadeUf from '../../components/CampoCidadeUf';
import DicaUltimoKm from '../../components/DicaUltimoKm';
import { erroDeKmMenor, useUltimoKm } from '../../hooks/useUltimoKm';
import { prepararImagemParaEnvio } from '../../utils/imagem';
import { DecimalInput, KmInput, LitrosInput } from '../../components/MaskedInputs';
import { formatCurrency, formatDate, formatKilometers } from '../../utils/formatters';
import { Veiculo } from '../../types';

/**
 * Lançamento de abastecimento ou despesa pelo próprio motorista.
 *
 * A tela assume que quem preenche está na rua, quase sempre no celular e com
 * pressa: campos grandes, um assunto por bloco e o comprovante fotografado na
 * hora. Como o motorista não pode editar depois de enviar, o envio passa
 * obrigatoriamente por um resumo de conferência — é a última chance de ele
 * ver o que está mandando.
 */

const COMBUSTIVEIS = [
  { valor: 'gasolina', rotulo: 'Gasolina' },
  { valor: 'etanol', rotulo: 'Etanol' },
  { valor: 'diesel', rotulo: 'Diesel' },
  { valor: 'gnv', rotulo: 'GNV' },
  { valor: 'flex', rotulo: 'Flex' },
];

/** Mesmo teto que o servidor aplica; avisar antes evita subir arquivo a toa. */
const MAX_ANEXOS = 10;

/**
 * Deixa o motorista escolher um arquivo já existente, além de fotografar.
 *
 * DESLIGADO por decisão de uso: o comprovante deve ser fotografado na hora. Com
 * o seletor de arquivos disponível, o motorista pode mandar qualquer coisa da
 * galeria — a foto de outro dia, um print, um arquivo que não é comprovante —, e
 * como ele não pode corrigir o lançamento depois, o gestor recebe o problema
 * sem meio de saber o que era para ser.
 *
 * O código fica no lugar, e não apagado, porque o anexo pode voltar a ser
 * necessário (nota que chega por e-mail, PDF do posto). Para reativar, troque
 * para `true`: o botão volta, o texto de ajuda se ajusta sozinho e o backend já
 * aceita PDF — nada mais precisa mudar.
 *
 * Vale saber o limite disto: `capture="environment"` faz o celular abrir a
 * câmera, mas no computador o navegador ignora o atributo e mostra o seletor de
 * arquivos do mesmo jeito. Isto é um direcionamento de uso, não uma trava.
 */
const PERMITE_ANEXAR_ARQUIVO = false;

const hoje = () => new Date().toISOString().slice(0, 10);

/**
 * Converte para número o que os campos mascarados guardam.
 *
 * Eles já entregam o valor em formato de máquina ("150.90"): o que o motorista
 * vê com vírgula é só a exibição. Tratar o ponto como separador de milhar aqui
 * transformaria 150,90 em 15090.
 */
const paraNumero = (texto: string): number => {
  const n = parseFloat(String(texto ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const Campo: React.FC<{
  label: string;
  para: string;
  obrigatorio?: boolean;
  children: React.ReactNode;
}> = ({ label, para, obrigatorio, children }) => (
  <div>
    <label htmlFor={para} className="block text-sm font-medium text-secondary-700 mb-1">
      {label} {obrigatorio && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const entrada =
  'block w-full rounded-lg border-secondary-300 py-3 text-base shadow-sm focus:border-primary-500 focus:ring-primary-500';

const LancamentoMotorista: React.FC = () => {
  const { tipo } = useParams<{ tipo: string }>();
  const navigate = useNavigate();
  const eAbastecimento = tipo !== 'despesa';

  const [dados, setDados] = useState<Record<string, string>>({
    veiculo_id: '',
    data_abastecimento: hoje(),
    data_despesa: hoje(),
    quilometragem: '',
    litros: '',
    preco_litro: '',
    tipo_combustivel: 'gasolina',
    posto: '',
    cidade: '',
    estado: '',
    categoria: '',
    descricao: '',
    valor: '',
    observacoes: '',
  });

  // Guarda nome e caminho juntos: o nome e o que o motorista reconhece na tela,
  // o caminho e o que o servidor precisa receber.
  const [anexos, setAnexos] = useState<Array<{ nome: string; caminho: string }>>([]);
  const [enviandoArquivo, setEnviandoArquivo] = useState(0);
  const [confirmando, setConfirmando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const inputCamera = useRef<HTMLInputElement>(null);
  const inputArquivo = useRef<HTMLInputElement>(null);

  // O catalogo e de cada empresa: uma lista fixa aqui mostraria categorias que
  // a empresa dele apagou, e esconderia as que ela criou.
  const { data: respostaCategorias } = useQuery({
    queryKey: ['portal', 'categorias-despesa'],
    queryFn: portalService.getCategoriasDeDespesa,
  });
  const categorias = respostaCategorias?.data ?? [];

  const { data: respostaVeiculos, isLoading: carregandoVeiculos } = useQuery({
    queryKey: ['portal', 'veiculos'],
    queryFn: portalService.getVeiculos,
  });
  const veiculos: Veiculo[] = (respostaVeiculos?.data as Veiculo[]) ?? [];
  // A lista traz so os veiculos atribuidos a ele. Sem nenhum, o motorista via um
  // seletor vazio e nao tinha como saber que faltava alguem atribuir na gestao.
  const semVeiculo = !carregandoVeiculos && veiculos.length === 0;

  const veiculoEscolhido = veiculos.find((v) => String(v.id) === dados.veiculo_id);

  // O km do painel vem preenchido com o ultimo registro do veiculo. O motorista
  // confere e corrige para cima; para baixo nao passa — nem aqui nem no servidor.
  const { ultimo: ultimoKm, carregando: buscandoKm } = useUltimoKm(eAbastecimento ? dados.veiculo_id : null, 'portal');

  useEffect(() => {
    if (!ultimoKm) return;
    // So preenche o que esta vazio: se ele ja digitou, o valor dele fica.
    setDados((d) => (d.quilometragem === '' ? { ...d, quilometragem: String(Math.round(ultimoKm.km)) } : d));
  }, [ultimoKm]);

  const erroKm = eAbastecimento ? erroDeKmMenor(dados.quilometragem, ultimoKm) : null;

  /**
   * O total do abastecimento: litros × preço, a mesma conta que o servidor faz.
   *
   * Calculado, e não digitado: pedir os três abriria espaço para não baterem
   * entre si. O comprovante não é lido; se o cupom fechar diferente, o gestor
   * ajusta olhando o anexo.
   */
  const totalDoAbastecimento = useMemo(
    () => Math.round(paraNumero(dados.litros) * paraNumero(dados.preco_litro) * 100) / 100,
    [dados.litros, dados.preco_litro],
  );

  const mudar = (campo: string, valor: string) => setDados((d) => ({ ...d, [campo]: valor }));

  const escolherArquivos = async (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;

    const escolhidos = Array.from(lista);
    const espaco = MAX_ANEXOS - anexos.length;

    if (espaco <= 0) {
      toast.error(`Máximo de ${MAX_ANEXOS} arquivos.`);
      return;
    }
    if (escolhidos.length > espaco) {
      toast.error(`Só cabem mais ${espaco} arquivo(s).`);
    }

    const aceitos = escolhidos.slice(0, espaco);
    setEnviandoArquivo((n) => n + aceitos.length);

    // Um de cada vez: o motorista costuma estar no 4G do posto, e várias fotos
    // subindo juntas competem pela mesma banda e demoram mais no total.
    for (const arquivoEscolhido of aceitos) {
      try {
        // Foto de iPhone sai em HEIC, que o servidor recusa e o Chrome nem abre:
        // vira JPEG aqui, no proprio aparelho.
        const arquivo = await prepararImagemParaEnvio(arquivoEscolhido);

        // O envio acontece na hora da escolha, não no submit: o motorista
        // descobre um arquivo recusado (tipo, tamanho) enquanto ainda está com
        // o papel na mão. O conteúdo não é lido: o anexo só é guardado.
        const resposta = await portalService.enviarComprovante(arquivo);
        const caminho = resposta.data?.path ?? '';
        if (caminho) {
          setAnexos((atuais) => [...atuais, { nome: arquivo.name, caminho }]);
        }
      } catch (erro: any) {
        toast.error(
          erro?.response?.data?.errors?.file?.[0]
            ?? `Não foi possível enviar ${arquivoEscolhido.name}.`,
        );
      } finally {
        setEnviandoArquivo((n) => n - 1);
      }
    }

    // Sem limpar, escolher o mesmo arquivo de novo não dispara o onChange.
    if (inputCamera.current) inputCamera.current.value = '';
    if (inputArquivo.current) inputArquivo.current.value = '';
  };

  const removerArquivo = (caminho: string) =>
    setAnexos((atuais) => atuais.filter((a) => a.caminho !== caminho));

  const abrirConferencia = (evento: React.FormEvent) => {
    evento.preventDefault();

    // O comprovante não é lido nem conferido: basta haver pelo menos um.
    if (anexos.length === 0) {
      toast.error('É obrigatório anexar pelo menos um comprovante.');
      return;
    }

    // Despesa: o valor é o que o motorista digitou.
    if (!eAbastecimento) {
      if (paraNumero(dados.valor) <= 0) {
        toast.error('Informe o valor da despesa.');
        return;
      }

      setConfirmando(true);
      return;
    }

    if (erroKm) {
      toast.error(erroKm);
      return;
    }

    setConfirmando(true);
  };

  const enviar = async () => {
    setSalvando(true);

    try {
      if (eAbastecimento) {
        await portalService.criarAbastecimento({
          veiculo_id: Number(dados.veiculo_id),
          data_abastecimento: dados.data_abastecimento,
          quilometragem: paraNumero(dados.quilometragem),
          litros: paraNumero(dados.litros),
          preco_litro: paraNumero(dados.preco_litro),
          tipo_combustivel: dados.tipo_combustivel,
          posto: dados.posto,
          cidade: dados.cidade,
          estado: dados.estado.toUpperCase(),
          comprovantes: anexos.map((a) => a.caminho),
          observacoes: dados.observacoes || undefined,
        });
      } else {
        await portalService.criarDespesa({
          veiculo_id: Number(dados.veiculo_id),
          categoria: dados.categoria,
          descricao: dados.descricao,
          valor: paraNumero(dados.valor),
          data_despesa: dados.data_despesa,
          comprovantes: anexos.map((a) => a.caminho),
          observacoes: dados.observacoes || undefined,
        });
      }

      toast.success('Enviado. O gestor pode ajustar se precisar.');
      navigate('/motorista');
    } catch (erro: any) {
      const erros = erro?.response?.data?.errors;
      const primeiro = erros ? (Object.values(erros)[0] as string[])?.[0] : null;
      toast.error(primeiro ?? erro?.response?.data?.message ?? 'Não foi possível enviar.');
      setConfirmando(false);
    } finally {
      setSalvando(false);
    }
  };

  /** Linhas do resumo de conferência, na ordem em que o motorista preencheu. */
  const resumo: Array<[string, string]> = eAbastecimento
    ? [
        ['Veículo', veiculoEscolhido ? `${veiculoEscolhido.placa} — ${veiculoEscolhido.marca} ${veiculoEscolhido.modelo}` : '—'],
        ['Data', formatDate(dados.data_abastecimento)],
        ['Quilometragem', formatKilometers(paraNumero(dados.quilometragem))],
        ['Combustível', COMBUSTIVEIS.find((c) => c.valor === dados.tipo_combustivel)?.rotulo ?? '—'],
        ['Litros', `${paraNumero(dados.litros).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} L`],
        ['Preço por litro', formatCurrency(paraNumero(dados.preco_litro))],
        ['Total (litros × preço)', formatCurrency(totalDoAbastecimento)],
        ['Posto', dados.posto],
        ['Local', `${dados.cidade}/${dados.estado.toUpperCase()}`],
      ]
    : [
        ['Veículo', veiculoEscolhido ? `${veiculoEscolhido.placa} — ${veiculoEscolhido.marca} ${veiculoEscolhido.modelo}` : '—'],
        ['Data', formatDate(dados.data_despesa)],
        ['Categoria', categorias.find((c) => c.slug === dados.categoria)?.nome ?? '—'],
        ['Descrição', dados.descricao],
        ['Valor', formatCurrency(paraNumero(dados.valor))],
      ];

  return (
    <div className="min-h-screen bg-secondary-50 pb-10">
      <header className="bg-white border-b border-secondary-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/motorista')}
            className="p-2 -ml-2 rounded-lg text-secondary-500 hover:bg-secondary-100"
            aria-label="Voltar"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-secondary-900">
            {eAbastecimento ? 'Novo abastecimento' : 'Nova despesa'}
          </h1>
        </div>
      </header>

      <form onSubmit={abrirConferencia} className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2 text-sm text-amber-900">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-500" />
          <p>
            Depois de enviar você não consegue alterar. Se errar alguma coisa, avise o gestor — ele corrige pelo
            sistema.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-secondary-200 p-4 space-y-4">
          {semVeiculo && (
            <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Nenhum veículo está atribuído a você, e por isso não dá para lançar nada ainda. Peça a quem
              administra a frota para atribuir o veículo que você dirige.
            </div>
          )}

          <Campo para="veiculo_id" label="Veículo" obrigatorio>
            <select
              id="veiculo_id"
              name="veiculo_id"
              value={dados.veiculo_id}
              onChange={(e) => mudar('veiculo_id', e.target.value)}
              required
              className={entrada}
            >
              <option value="">{semVeiculo ? 'Nenhum veículo atribuído a você' : 'Selecione o veículo'}</option>
              {veiculos.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.placa} — {v.marca} {v.modelo}
                </option>
              ))}
            </select>
          </Campo>

          <Campo para="data" label="Data" obrigatorio>
            <input
              id="data"
              type="date"
              max={hoje()}
              value={eAbastecimento ? dados.data_abastecimento : dados.data_despesa}
              onChange={(e) => mudar(eAbastecimento ? 'data_abastecimento' : 'data_despesa', e.target.value)}
              required
              className={entrada}
            />
          </Campo>
        </div>

        {eAbastecimento ? (
          <div className="bg-white rounded-xl border border-secondary-200 p-4 space-y-4">
            <div>
              <KmInput
                name="quilometragem"
                label="Quilometragem do painel"
                value={dados.quilometragem}
                onChange={(e) => mudar('quilometragem', e.target.value)}
                required
                className={entrada}
              />
              <DicaUltimoKm ultimo={ultimoKm} erro={erroKm} carregando={buscandoKm} />
            </div>

            <Campo para="tipo_combustivel" label="Combustível" obrigatorio>
              <select
                id="tipo_combustivel"
                value={dados.tipo_combustivel}
                onChange={(e) => mudar('tipo_combustivel', e.target.value)}
                required
                className={entrada}
              >
                {COMBUSTIVEIS.map((c) => (
                  <option key={c.valor} value={c.valor}>
                    {c.rotulo}
                  </option>
                ))}
              </select>
            </Campo>

            <div className="grid grid-cols-2 gap-4">
              <LitrosInput
                name="litros"
                label="Litros"
                ajuda="Use vírgula para os decimais, como está na bomba: 112,676"
                value={dados.litros}
                onChange={(e) => mudar('litros', e.target.value)}
                required
                className={entrada}
              />
              <DecimalInput
                name="preco_litro"
                label="Preço por litro"
                casas={3}
                value={dados.preco_litro}
                onChange={(e) => mudar('preco_litro', e.target.value)}
                required
                className={entrada}
              />
            </div>

            {/* O total nao e digitado: sai de litros x preco, como no servidor. */}
            <div className="rounded-lg bg-secondary-50 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-secondary-600">
                Total
                <span className="block text-xs text-secondary-500">Litros × preço por litro</span>
              </span>
              <span className="text-lg font-semibold text-secondary-900">
                {formatCurrency(totalDoAbastecimento)}
              </span>
            </div>

            <Campo para="posto" label="Posto" obrigatorio>
              <input
                id="posto"
                value={dados.posto}
                onChange={(e) => mudar('posto', e.target.value)}
                required
                maxLength={100}
                placeholder="Nome do posto"
                className={entrada}
              />
            </Campo>

            {/* UF e cidade saem da lista do IBGE: no celular, escolher e mais
                rapido do que digitar, e o nome do municipio chega sempre escrito
                do mesmo jeito ao relatorio. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CampoCidadeUf
                uf={dados.estado}
                cidade={dados.cidade}
                required
                onChange={({ uf, cidade }) => setDados((d) => ({ ...d, estado: uf, cidade }))}
              />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-secondary-200 p-4 space-y-4">
            <Campo para="categoria" label="Categoria" obrigatorio>
              <select
                id="categoria"
                value={dados.categoria}
                onChange={(e) => mudar('categoria', e.target.value)}
                required
                className={entrada}
              >
                <option value="">Selecione a categoria</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.slug}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </Campo>

            <Campo para="descricao" label="Descrição" obrigatorio>
              <input
                id="descricao"
                value={dados.descricao}
                onChange={(e) => mudar('descricao', e.target.value)}
                required
                maxLength={255}
                placeholder="Ex.: troca de pneu dianteiro"
                className={entrada}
              />
            </Campo>

            {/* Digitado: o comprovante da despesa nao e lido, so anexado. O
                gestor confere o numero pelo anexo. */}
            <DecimalInput
              name="valor"
              label="Valor (R$)"
              value={dados.valor}
              onChange={(e) => mudar('valor', e.target.value)}
              required
              className={entrada}
            />
          </div>
        )}

        <div className="bg-white rounded-xl border border-secondary-200 p-4 space-y-3">
          <h2 className="text-sm font-medium text-secondary-700">
            Comprovante <span className="text-red-500">*</span>
          </h2>

          {anexos.length > 0 && (
            <ul className="space-y-2">
              {anexos.map((anexo) => (
                <li key={anexo.caminho}
                  className="flex items-center gap-3 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-3">
                  <FileText className="w-5 h-5 text-secondary-400 flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-secondary-700 truncate">{anexo.nome}</span>
                    <span className="block text-xs text-secondary-400">Anexado</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removerArquivo(anexo.caminho)}
                    className="p-1 rounded text-secondary-400 hover:text-red-600"
                    aria-label={`Remover ${anexo.nome}`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {enviandoArquivo > 0 && (
            <div className="flex items-center gap-2 text-sm text-secondary-600">
              <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
              Enviando {enviandoArquivo} arquivo(s)...
            </div>
          )}

          {/* Os botoes continuam visiveis depois do primeiro anexo: o cupom e o
              comprovante de pagamento sao duas fotos, tiradas uma apos a outra. */}
          {anexos.length < MAX_ANEXOS && (
            <div className={`grid gap-3 ${PERMITE_ANEXAR_ARQUIVO ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {/* capture="environment" faz o celular abrir a camera traseira
                  direto, em vez da galeria. No computador o navegador ignora
                  o atributo e cai no seletor de arquivos normal. */}
              <button
                type="button"
                onClick={() => inputCamera.current?.click()}
                className="flex flex-col items-center gap-1 rounded-lg border-2 border-dashed border-secondary-300 py-5 text-secondary-600 hover:border-primary-400 hover:text-primary-600"
              >
                <Camera className="w-6 h-6" />
                <span className="text-sm font-medium">Tirar foto</span>
              </button>
              {PERMITE_ANEXAR_ARQUIVO && (
                <button
                  type="button"
                  onClick={() => inputArquivo.current?.click()}
                  className="flex flex-col items-center gap-1 rounded-lg border-2 border-dashed border-secondary-300 py-5 text-secondary-600 hover:border-primary-400 hover:text-primary-600"
                >
                  <Paperclip className="w-6 h-6" />
                  <span className="text-sm font-medium">Anexar arquivo</span>
                </button>
              )}
            </div>
          )}

          <input
            ref={inputCamera}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => escolherArquivos(e.target.files)}
          />
          {PERMITE_ANEXAR_ARQUIVO && (
            <input
              ref={inputArquivo}
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={(e) => escolherArquivos(e.target.files)}
            />
          )}

          <p className="text-xs text-secondary-500">
            É obrigatório anexar pelo menos um comprovante.{' '}
            {PERMITE_ANEXAR_ARQUIVO
              ? `Foto do celular (inclusive HEIC do iPhone) ou PDF, até 10 MB cada. Você pode anexar até ${MAX_ANEXOS}.`
              : eAbastecimento
              ? `Fotografe o cupom ou o comprovante do posto. Você pode tirar até ${MAX_ANEXOS} fotos.`
              : `Fotografe o comprovante da despesa (nota, recibo). Você pode tirar até ${MAX_ANEXOS} fotos.`}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-secondary-200 p-4">
          <Campo para="observacoes" label="Observações">
            <textarea
              id="observacoes"
              rows={3}
              maxLength={500}
              value={dados.observacoes}
              onChange={(e) => mudar('observacoes', e.target.value)}
              placeholder="Algo que o gestor precise saber"
              className={entrada}
            />
          </Campo>
        </div>

        <button
          type="submit"
          disabled={enviandoArquivo > 0}
          className="w-full rounded-lg bg-primary-600 py-4 text-base font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
        >
          Conferir e enviar
        </button>
      </form>

      {confirmando && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
          <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col">
            <div className="px-5 pt-5 pb-3 border-b border-secondary-200">
              <h2 className="text-lg font-semibold text-secondary-900">Confira antes de enviar</h2>
              <p className="text-sm text-secondary-600 mt-1">Depois de enviar você não consegue alterar.</p>
            </div>

            <dl className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {resumo.map(([rotulo, valor]) => (
                <div key={rotulo} className="flex justify-between gap-4 text-sm py-1">
                  <dt className="text-secondary-500 flex-shrink-0">{rotulo}</dt>
                  <dd className="text-secondary-900 font-medium text-right break-words">{valor || '—'}</dd>
                </div>
              ))}
              <div className="flex justify-between gap-4 text-sm py-1">
                <dt className="text-secondary-500 flex-shrink-0">
                  {anexos.length === 1 ? 'Comprovante' : 'Comprovantes'}
                </dt>
                {/* Lista os nomes em vez do total: na hora de conferir, o que o
                    motorista reconhece e o arquivo, nao a quantidade. */}
                <dd className="text-secondary-900 font-medium text-right break-words">
                  {anexos.map((a) => a.nome).join(', ')}
                </dd>
              </div>
            </dl>

            <div className="px-5 py-4 border-t border-secondary-200 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                disabled={salvando}
                className="rounded-lg border border-secondary-300 py-3 font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
              >
                Corrigir
              </button>
              <button
                type="button"
                onClick={enviar}
                disabled={salvando}
                className="rounded-lg bg-primary-600 py-3 font-semibold text-white hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>{salvando ? 'Enviando...' : 'Confirmar envio'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LancamentoMotorista;
